import { Box, Chip, Typography, IconButton } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { fieldDisplayNames } from "./records/activityPropsRecordsUtility";
import { ChangedFields, SummaryProps } from "./types/workOrderActivityTypes";
import { useState, useEffect } from "react";

const ChangeSummary = ({
  changedFieldsJson,
  isCollapsed = false,
  onToggleCollapse,
}: SummaryProps) => {
  const [isIndividualCollapsed, setIsIndividualCollapsed] =
    useState<boolean>(true);
  const [hasUserInteracted, setHasUserInteracted] = useState<boolean>(false);

  // Sync individual state with global state when global state changes
  useEffect(() => {
    // Only sync if user hasn't interacted OR if global state has changed from previous value
    if (!hasUserInteracted) {
      setIsIndividualCollapsed(isCollapsed);
    }
  }, [isCollapsed]); // Remove hasUserInteracted from dependencies

  // Reset user interaction when global state changes
  useEffect(() => {
    if (hasUserInteracted) {
      setHasUserInteracted(false);
      setIsIndividualCollapsed(isCollapsed);
    }
  }, [isCollapsed]);

  // Determine if this item should be collapsed
  // If user has interacted, prioritize individual state
  // Otherwise, use global state
  const shouldBeCollapsed = hasUserInteracted
    ? isIndividualCollapsed
    : isCollapsed;

  // Handle individual toggle
  const handleIndividualToggle = () => {
    setHasUserInteracted(true);
    setIsIndividualCollapsed(!isIndividualCollapsed);
    if (onToggleCollapse) {
      onToggleCollapse();
    }
  };

  let parsed: ChangedFields;

  try {
    parsed = JSON.parse(changedFieldsJson);
  } catch (error) {
    return <div>Invalid change data</div>;
  }

  // Render collapsed version (summary only)
  if (shouldBeCollapsed) {
    const fieldCount = Object.values(parsed).reduce(
      (acc, fields) => acc + Object.keys(fields).length,
      0
    );
    return (
      <Box
        ml={"5px"}
        sx={{
          padding: "5px",
          borderRadius: "5px",
          background: (theme) =>
            theme.palette.mode === "light" ? "white" : "#181f2a",
          boxShadow: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
        onClick={handleIndividualToggle}
      >
        <Typography
          variant="body2"
          sx={{ fontStyle: "italic", color: "text.secondary" }}
        >
          {fieldCount} field{fieldCount !== 1 ? "s" : ""} changed
        </Typography>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleIndividualToggle();
          }}
          sx={{ p: 0.5 }}
        >
          <ExpandMoreIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  }

  // Render full version
  return (
    <Box
      ml={"5px"}
      sx={{
        padding: "5px",
        borderRadius: "5px",
        background: (theme) =>
          theme.palette.mode === "light" ? "white" : "#181f2a",
        boxShadow: 2,
        cursor: "pointer",
      }}
      onClick={handleIndividualToggle}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          Changed Fields:
        </Typography>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleIndividualToggle();
          }}
          sx={{ p: 0.5 }}
        >
          <ExpandLessIcon fontSize="small" />
        </IconButton>
      </Box>
      {Object.entries(parsed).map(([entity, fields]) => (
        <Box key={entity} mb="3px">
          {Object.entries(fields).map(([fieldName, values]) => {
            const displayName = fieldDisplayNames[fieldName] || fieldName; // fallback to fieldName
            return (
              <Typography variant="body2" key={fieldName} component="div">
                <strong>{displayName}</strong> was updated from{" "}
                <Typography variant="body2" component="span" color="text.secondary">
                  <span
                    style={{ textDecoration: "line-through", color: "#d32f2f" }}
                  >
                    {values.old || "empty"}
                  </span>{" "}
                  <span
                    style={{
                      fontSize: "24px",
                      fontWeight: "bold",
                      verticalAlign: "sub",
                    }}
                  >
                    →
                  </span>{" "}
                  <span style={{ color: "#2e7d32" }}>
                    {values.newValue || "empty"}
                  </span>
                </Typography>
              </Typography>
            );
          })}
        </Box>
      ))}
    </Box>
  );
};

export default ChangeSummary;
